import { useP4ToastController } from '../../views/system/NotificationsArea';
import React, { useContext } from 'react';
import { InfoButton } from '../../components/connectedForm/Button';
import getToastId from '../../utils/getToastId';
import Notification from '../../components/Notification';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useMutation } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import SalesOrderNotifyMaxoptraMutation from '../../graphql/mutations/sales/SalesOrderNotifyMaxoptraMutation.graphql';
import SalesOrderLinesRefreshMaxoptraTrackingMutation from '../../graphql/mutations/sales/SalesOrderLinesRefreshMaxoptraTrackingMutation.graphql';
import MaxoptraOrdersQuery from '../../graphql/queries/sales/MaxoptraOrdersQuery.graphql';
import SalesOrderLineWorkspaceQuery from '../../graphql/queries/sales/SalesOrderLineWorkspaceQuery.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import {
  AlertType,
  IMaxoptraNotificationResult,
  ISalesOrderLine,
  ISalesOrderLinesRefreshMaxoptraTrackingMutationResult,
  ISalesOrderLinesRefreshMaxoptraTrackingMutationVariables,
  ISalesOrderNotifyMaxoptraMutationResult,
  ISalesOrderNotifyMaxoptraMutationVariables,
  MaxoptraOption,
  Maybe,
} from '../../graphql/schema/p4types';
import { IActionCriteria } from '../../components/document/DocumentProvider';

export const useDisplayMaxoptraNotificationResults = () => {
  const { dispatchToast, dispatchSimpleToast, dismissToast } = useP4ToastController();

  const displayMaxoptraNotificationResults = (
    notificationResult: IMaxoptraNotificationResult,
    notifyMaxoptra: (option?: MaxoptraOption) => void
  ) => {
    let toastId = '';
    const options = notificationResult?.options || [];
    const linesNotifiedForUpdate = notificationResult?.linesNotifiedForUpdate || [];
    const linesNotifiedForCreate = notificationResult?.linesNotifiedForCreate || [];
    const linesFailingGroupCodeValidation = notificationResult?.linesFailingGroupCodeValidation || [];
    const linesInLockedDelivery = notificationResult?.linesInLockedDelivery || [];

    let message = '';
    const notificationActionOptions: React.ReactElement[] = [];

    if (options.length > 0) {
      if (options.includes(MaxoptraOption.Update)) {
        notificationActionOptions.push(
          <InfoButton
            key="updateMaxoptraOrder"
            onClick={() => {
              dismissToast(toastId);
              notifyMaxoptra(MaxoptraOption.Update);
            }}
          >
            Update Existing
          </InfoButton>
        );
        message = 'There is an existing Maxoptra order';
      }

      if (options.includes(MaxoptraOption.Create)) {
        notificationActionOptions.push(
          <InfoButton
            key="createMaxoptraOrder"
            onClick={() => {
              dismissToast(toastId);
              notifyMaxoptra(MaxoptraOption.Create);
            }}
          >
            Create New
          </InfoButton>
        );
      }

      toastId = getToastId();
      dispatchToast(
        <Notification
          title={'Notify Maxoptra'}
          message={message}
          type={AlertType.Action}
          actionButtons={notificationActionOptions}
        />,
        {
          toastId: toastId,
          title: 'Notify Maxoptra',
          type: AlertType.Action,
          message: message,
        }
      );
      return;
    }

    if (notificationResult.success) {
      if (linesNotifiedForCreate.length + linesNotifiedForUpdate.length === 0) {
        dispatchSimpleToast('Notify Maxoptra', 'No valid lines to notify for', AlertType.Warning);
      }

      if (notificationResult.maxoptraOrderCreated && linesNotifiedForCreate.length > 0) {
        dispatchSimpleToast(
          'Maxoptra Order Created For',
          formatAndJoinLines(linesNotifiedForCreate),
          AlertType.Success
        );
      }

      if (linesNotifiedForUpdate.length > 0) {
        dispatchSimpleToast(
          'Existing Maxoptra Orders Updated For',
          formatAndJoinLines(linesNotifiedForUpdate),
          AlertType.Success
        );
      }

      if (linesFailingGroupCodeValidation.length > 0) {
        dispatchSimpleToast(
          'Lines Failing Group Code Validation',
          formatAndJoinLines(linesFailingGroupCodeValidation),
          AlertType.Warning
        );
      }

      if (linesInLockedDelivery.length > 0) {
        dispatchSimpleToast('Lines In Locked Delivery', formatAndJoinLines(linesInLockedDelivery), AlertType.Warning);
      }
    } else {
      dispatchSimpleToast(
        'Notify Maxoptra',
        notificationResult?.failureMessage ?? 'An error occurred',
        AlertType.Error
      );
    }
  };

  const formatAndJoinLines = (lines: any[]): string => {
    return lines.map((value) => formatSalesOrderLineDescription(value?.salesOrderLine)).join('\n');
  };

  const formatSalesOrderLineDescription = (orderLine: Maybe<ISalesOrderLine>): string => {
    return orderLine?.product?.description ?? '';
  };

  return { displayMaxoptraNotificationResults };
};

export const useNotifyMaxoptraForSalesOrder = (salesOrderId: number | undefined): (() => void) => {
  const connectedForm = useContext(ConnectedFormContext);
  const handleQueryError = useComponentQueryErrorHandling();
  const { displayMaxoptraNotificationResults } = useDisplayMaxoptraNotificationResults();

  const [notifyMaxoptraForSalesOrderMutation] = useMutation<
    ISalesOrderNotifyMaxoptraMutationResult,
    ISalesOrderNotifyMaxoptraMutationVariables
  >(SalesOrderNotifyMaxoptraMutation, {
    refetchQueries: [MaxoptraOrdersQuery],
    onError: handleQueryError,
    onCompleted: () => {
      connectedForm?.reloadData?.(true);
    },
  });

  const notifyMaxoptraForSalesOrder = (option?: MaxoptraOption) => {
    notifyMaxoptraForSalesOrderMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesOrderId: salesOrderId,
          userDecision: option,
        },
      },
    }).then((result) => {
      const notificationResult = result?.data?.maxoptraNotifySalesOrder?.result as IMaxoptraNotificationResult;
      displayMaxoptraNotificationResults(notificationResult, notifyMaxoptraForSalesOrder);
    });
  };

  return notifyMaxoptraForSalesOrder;
};

export const useRefreshMaxoptraTrackingForSalesOrderLines = (): ((actionCriteria: IActionCriteria) => void) => {
  const handleQueryError = useComponentQueryErrorHandling();
  const { dispatchSimpleToast } = useP4ToastController();
  const connectedForm = useContext(ConnectedFormContext);

  const [refreshMaxoptraTrackingForSalesOrderLinesMutation] = useMutation<
    ISalesOrderLinesRefreshMaxoptraTrackingMutationResult,
    ISalesOrderLinesRefreshMaxoptraTrackingMutationVariables
  >(SalesOrderLinesRefreshMaxoptraTrackingMutation, {
    refetchQueries: [MaxoptraOrdersQuery, SalesOrderLineWorkspaceQuery],
    onError: handleQueryError,
    onCompleted: () => {
      connectedForm?.reloadData?.(true);
      dispatchSimpleToast('Refresh Tracking', 'Maxoptra tracking refreshed successfully', AlertType.Success);
    },
  });

  return (actionCriteria: IActionCriteria) => {
    refreshMaxoptraTrackingForSalesOrderLinesMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria: actionCriteria,
        },
      },
    });
  };
};
